/**
 * Licensed Materials - Property of IBM
 *
 * IBM Cognos Products: BI Glass
 *
 * Copyright IBM Corp. 2020
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'react',
	'prop-types',
	'ba-ui-carbon',
	'ba-ui-carbon-toolkit',
	'../utils/CreateAssetItemGroups',
	'../../nls/StringResources'
], (React, PropTypes, Carbon, Toolkit, CreateAssetItemGroups, StringResources) => {

	const { Loading } = Carbon;
	const { Slideout } = Toolkit;

	return class CreateAssetMenu extends React.Component {

		static propTypes = {
			menuSpec: PropTypes.any,
			Glass: PropTypes.any,
			menuId: PropTypes.string,
			onHidden: PropTypes.function
		}

		constructor(props) {
			super(props);
			this.state = {
				loading: true,
				active: false,
				items: []
			};
			this.props.Glass.externalMenuSupport.getVisibleItems(this.props.menuSpec).then(items => {
				this.setState({ items, loading: false });
			}).catch(err => {
				this.props.Glass.getCoreSvc('.Logger').warn(err);
				this.setState({ items: [], loading: false });
			});
			this._listRef = React.createRef();
		}

		onSelectItem(item) {
			this.props.Glass.externalMenuSupport.handleMenuAction(item.featureId, item.id, this.props.menuSpec);
		}

		_renderGroupHeader(group) {
			return (
				<li role="group"
					tabIndex="-1"
					style={{ 'pointer-events': 'none' }}
					className="create-menu-item"
					aria-label={group.label}
					key={group.id}>
					<a className="create-menu-link header" role="menuitem">
						<span title={group.label} dir="ltr" aria-label={group.label}>{group.label}</span>
					</a>
				</li>
			);
		}

		_renderGroupItem(group, item) {
			return (
				<li id={item.id} role="group" tabIndex="0" onKeyDown={this.onKeyDown.bind(this, item)} className={`create-menu-item`} aria-label={item.label} key={item.id} onClick={this.onSelectItem.bind(this, item)}>
					<a className="create-menu-link item" role="menuitem">
						<svg className={`svgIcon ${group.className}`} role="img" focusable="false">
							<use style={{ 'pointer-events': 'none' }} xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref={`#${item.icon}`} />
						</svg>
						<span title={item.label} dir="ltr" className="hasIcon" aria-label={item.label}>{item.label}</span>
					</a>
				</li>
			);
		}

		renderGroups(groups) {
			return groups.map(group => {
				return [this._renderGroupHeader(group)].concat(group.items.map(this._renderGroupItem.bind(this, group)));
			});
		}

		_getFocusableElements() {
			if ( this._listRef.current ) {
				const allFocusableElements = this._listRef.current.querySelectorAll('li');
				const elements = [];
				allFocusableElements.forEach(el => {
					if ( el.tabIndex === 0 ) {
						elements.push(el);
					}
				});
				return elements;
			} else {
				return [];
			}
		}

		_focusFirstElement() {
			const focusableContent = this._getFocusableElements();
			if ( focusableContent.length > 0 ) {
				focusableContent[0].focus();
			}
		}

		// Focus Trapping for A11Y
		onKeyDown(item, evt) {
			const keycode = evt.keyCode || evt.which;
			if ( keycode === 9 && this._listRef.current ) {
				const focusableContent = this._getFocusableElements();
				if ( focusableContent.length > 0 ) {
					const firstEl = focusableContent[0];
					const lastEl = focusableContent[focusableContent.length - 1];
					if (evt.shiftKey) { // if shift key pressed for shift + tab combination
						if (document.activeElement === firstEl) {
							lastEl.focus(); // add focus for the last focusable element
							evt.preventDefault();
						}
					} else {
						if (document.activeElement === lastEl) {
							firstEl.focus();
							evt.preventDefault();
						}
					}
				}
			} else if ( (keycode === 13) || (keycode === 32) ) {
				this.onSelectItem(item);
				this.props.Glass.hideOpenMenus();
			}
		}

		render() {
			const { items, active, loading } = this.state;
			const groups = CreateAssetItemGroups(items);
			return (
				<Slideout
					width='320px'
					className="ba-portal-carbon-create-menu"
					id={this.props.menuId}
					active={active}
					onKeyUp={evt => {
						const keycode = evt.keyCode || evt.which;
						if ( keycode === 27 /* esc */ || keycode === 37 /* left-arrow */) {
							this._setFocus = true;
							return this.setState({ active: false });
						}
					}}
					onHidden={() => {
						if (this._setFocus) {
							this._setFocus = false;
							this.props.onHidden();
						}
					}}
					onOpened={() => {
						if ( this._setFocus ) {
							this._setFocus = false;
							this._focusFirstElement();
						}
					}}
					onClickAway={() => {}}
				>
					<ul role="menu" tabIndex="-1" className="create-menu-items" ref={this._listRef}>
						<li className="create-menu-item create-menu-header" aria-label={StringResources.get('createMenu_title')} tabIndex="-1">{StringResources.get('createMenu_title')}</li>
						{loading && <Loading
							description={StringResources.get('createMenu_loading')}
							withOverlay={false}
						/>}
						{!loading && this.renderGroups(groups)}
					</ul>
				</Slideout>
			);
		}

		toggleState(setFocus) {
			this._setFocus = setFocus;
			const { active } = this.state;
			this.setState({ active: !active });
		}

		closeMenu() {
			this._setFocus = false;
			this.setState({ active: false });
		}
	};

});